import { createClient } from '@supabase/supabase-js'
import { env } from '../config/env'

const supabaseUrl = env.VITE_SUPABASE_URL
const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY

// Snapshot the URL synchronously at module load — BEFORE the client below is
// created and its detectSessionInUrl asynchronously consumes and CLEARS the
// recovery/invite fragment (auth-js `_getSessionFromURL` -> `window.location.hash = ''`).
// This module is imported eagerly at app boot (main.tsx -> lib/auth-bridge), so
// this runs before any async work. It records whether THIS page load began as a
// genuine Supabase auth callback, letting useAuthTokenValidation tell a real
// recovery/invite link (whose fragment the SDK already consumed) apart from an
// ordinary pre-existing session — an authenticated user opening /accept-invitation
// or /reset-password with no link token must NOT be treated as a valid link.
//
// We require BOTH tokens of the implicit-flow pair to be present in the URL
// *fragment*, which is the exact and only shape GoTrue emits for a
// recovery/invite/signup redirect here (this client uses the implicit flow, not
// PKCE). This deliberately does NOT accept a lone `access_token`, anything in the
// query string, or a bare `?code=`: those are not proof of a callback and would
// let a stray param on the auth pages promote an ordinary session into a valid
// link. A forged/partial fragment cannot slip through either — if the fragment
// tokens are not valid, detectSessionInUrl leaves them in place, so
// useAuthTokenValidation's Step 2 (setSession from URL) runs and reports the
// failure via `linkError` (the page then offers the recovery panel instead of
// bouncing to /login, #1535), and the getSession() fallback is never reached.
export const initialUrlWasAuthCallback = ((): boolean => {
  if (typeof window === 'undefined') return false
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  return hash.has('access_token') && hash.has('refresh_token')
})()

// ONE canonical supabase-js client. The SDK owns the session in localStorage
// (the default storage) and silently refreshes the access token. App state is
// driven by a single supabase.auth.onAuthStateChange listener — see
// lib/auth-bridge.ts, whose initAuthBridge() is invoked once at app init in
// main.tsx. There is no fetch-level token reroute and no server-side token plumbing.
// Login goes straight to GoTrue via signInWithPassword.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
})
